import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { flatten, map } from 'lodash-es';
import apiI18n from 'emailApp/i18n'; /* eslint-disable-line import/no-unresolved */
import { ConfirmEmail } from '@/components/ConfirmEmail';
import { I18nextProvider } from 'react-i18next';
import * as routes from '@/services/dictionaries/Routes';
import ForgotPassword from '@/components/ForgotPassword';
import Login from '@/components/Login';
import Register from '@/components/Register';
import RegisterActivatedUser from '@/components/RegisterActivatedUser';
import RegisterUser from '@/components/RegisterUser';
import ResetPassword from '@/components/ResetPassword';
import { AccountDeleted } from '@/components/AccountDeleted';

import Auth from '../Authentication';
import Unauthorized from '../errors/401';
import NotFound from '../errors/404';
import InternalServer from '../errors/500';
import ServiceUnavailable from '../errors/503';
import LegacyDashRoute from './LegacyDashRoute';
import PrivateRoute from './PrivateRoute';
import { CampaignsPaths, ContactsPaths, HomePaths } from '../dictionaries/Paths';
import { ApiRoutes } from '../../dashboards/EmailApi/routes';
import { useUpdateIntercom } from '../hooks/useUpdateIntercom/index';
import { RedirectRoutes } from './RedirectRoutes';

const ApiRoutesWithProvider = map(ApiRoutes, (route) => ({
    ...route,
    ...{
        component: (props: any): JSX.Element => (
            <I18nextProvider i18n={apiI18n}>
                <route.component {...props} />
            </I18nextProvider>
        ),
    },
}));

const routesWithRemotes = flatten([...Object.values(routes), ...ApiRoutesWithProvider]);

const Router: React.FC = () => {
    useUpdateIntercom();
    return (
        <Switch>
            <Route component={Login} path={HomePaths.Login} />
            <Route exact component={Register} path={HomePaths.CreateAccount} />
            <Route exact component={ConfirmEmail} path={HomePaths.ConfirmEmail} />
            <Route exact component={RegisterUser} path={HomePaths.RegisterUser} />
            <Route exact component={RegisterActivatedUser} path={HomePaths.RegisterActivatedUser} />
            <Route exact component={ForgotPassword} path={HomePaths.Forgot} />
            <Route exact component={ResetPassword} path={HomePaths.ResetPassword} />
            <Route exact component={Unauthorized} path={HomePaths.Unauthorized} />
            <Route exact component={NotFound} path={HomePaths.NotFound} />
            <Route exact component={InternalServer} path={HomePaths.InternalServerError} />
            <Route exact component={ServiceUnavailable} path={HomePaths.SERVICE_UNAVAILABLE} />
            <Route exact component={AccountDeleted} path={HomePaths.AccountDeleted} />
            <Route exact path={HomePaths.Campaigns}>
                <Redirect to={CampaignsPaths.Campaigns.replace(':view?', '')} />
            </Route>
            <Route exact path={ContactsPaths.ContactsOld}>
                <Redirect to={ContactsPaths.Contacts} />
            </Route>
            <Route exact path={ContactsPaths.Contacts}>
                <Redirect to={ContactsPaths.ContactsOverview} />
            </Route>
            <PrivateRoute
                exact
                accessLevel={[AccessLevel.ViewAccount]}
                component={LegacyDashRoute}
                path="/legacy"
                disableBanners={true}
            />
            <PrivateRoute
                exact
                accessLevel={[AccessLevel.ViewAccount]}
                component={LegacyDashRoute}
                path="/legacy/#/forms"
                disableBanners={true}
            />
            <PrivateRoute
                accessLevel={[AccessLevel.ViewAccount]}
                component={LegacyDashRoute}
                path="/legacy/#/campaigncreator/target"
                disableBanners={true}
            />
            <PrivateRoute
                exact
                accessLevel={[AccessLevel.ViewAccount]}
                component={LegacyDashRoute}
                path="/legacy/#/campaigncreator"
                disableBanners={true}
            />
            <PrivateRoute
                accessLevel={[AccessLevel.ModifyTemplates]}
                component={LegacyDashRoute}
                path="/legacy/#/templates/edit/"
                disableBanners={true}
            />
            {routesWithRemotes.map(
                (singleRoute, index): JSX.Element => (
                    <PrivateRoute
                        key={`route-${index}`}
                        exact={!singleRoute.isNotExact}
                        path={singleRoute.path}
                        component={singleRoute.component}
                        accessLevel={singleRoute.accessLevel}
                        disableBanners={singleRoute.disableBanners}
                        upsellingFeature={singleRoute.upsellingFeature}
                    />
                )
            )}
            {!Auth.isAuthenticated() && <PrivateRoute component={Login} />}
            <RedirectRoutes />
            <Redirect to={HomePaths.NotFound} />
        </Switch>
    );
};

export default connect()(Router);
